import { useState } from 'react';
import { ChevronDown, ChevronRight, History, Mail, MailCheck, Send } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RunStatusBadge } from '@/components/ui/RunStatusBadge';
import { ManuelleWartungDialog } from '@/components/customers/ManuelleWartungDialog';
import { formatDateTime } from '@/lib/format';
import { useSendNotification } from '@/hooks/useMail';
import { isTestModeActive, useMailSettings } from '@/hooks/useMailSettings';
import {
  useCreateMaintenanceReportMail,
  useMaintenanceHistory,
  useMaintenanceSessionDetail,
  type MaintenanceReportMailResult,
  type MaintenanceSessionHistoryItem,
} from '@/hooks/useMaintenanceSession';

/**
 * Historie der Wartungsfenster eines Kunden.
 *
 * Wozu: Bis hierher war ein Fenster nach dem Schließen aus dem UI
 * verschwunden - die Kundenseite zeigte nur das gerade offene. Fragt ein Kunde
 * Tage später nach, was an einem Abend passiert ist, gab es keinen Weg zu der
 * Antwort.
 *
 * Zwei Ebenen, damit die Seite nicht bei jedem Aufruf die halbe Historie
 * nachlädt: die LISTE kommt in einem Rutsch (nur Metadaten), die ZAHLEN und
 * Läufe eines Fensters erst beim Aufklappen. Aufgeklappt wird immer nur eines
 * - mehrere offene Detailabfragen braucht niemand, und das hält die Seite
 * ruhig.
 *
 * Der Knopf "Bericht anfordern" legt eine ausführliche Mail als ENTWURF im
 * Postausgang ab. Versendet wird nichts: den Text liest der Betreiber, bevor
 * der Kunde ihn liest.
 */

interface Props {
  customerId: string;
  /**
   * Archivierter Kunde: die Historie bleibt lesbar, aber "Wartung erfassen"
   * verschwindet. Das Backend weist die Nacherfassung an einem archivierten
   * Kunden mit 404 ab - ein Knopf, der nach dem Ausfüllen des Dialogs in
   * einen Fehler läuft, ist schlimmer als kein Knopf.
   */
  readOnly?: boolean;
}

function dauerText(von: string, bis: string | null): string {
  if (!bis) return 'läuft noch';
  const ms = new Date(bis).getTime() - new Date(von).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '–';
  const minuten = Math.floor(ms / 60_000);
  if (minuten < 1) return 'unter 1 Minute';
  const stunden = Math.floor(minuten / 60);
  const rest = minuten % 60;
  if (stunden && rest) return `${stunden} Std. ${rest} Min.`;
  if (stunden) return `${stunden} Std.`;
  return minuten === 1 ? '1 Minute' : `${minuten} Minuten`;
}

const tagFormatter = new Intl.DateTimeFormat('de-AT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Vienna',
});

function tagUndZeit(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '–' : tagFormatter.format(d);
}

export function MaintenanceHistoryCard({ customerId, readOnly = false }: Props): JSX.Element {
  const query = useMaintenanceHistory(customerId);
  const [offenesFenster, setOffenesFenster] = useState<string | null>(null);
  const [dialogOffen, setDialogOffen] = useState(false);

  const fenster = query.data ?? [];

  return (
    <Card
      data-testid="maintenance-history-card"
      title={
        <span className="inline-flex items-center gap-2">
          <History className="size-4" aria-hidden="true" />
          Wartungshistorie
        </span>
      }
      description="Vergangene Wartungsfenster. Aufklappen zeigt, was an dem Abend lief."
      actions={
        readOnly ? undefined : (
          <Button
            variant="secondary"
            size="sm"
            data-testid="wartung-erfassen-oeffnen"
            onClick={() => setDialogOffen(true)}
          >
            Wartung erfassen
          </Button>
        )
      }
    >
      {query.isLoading && <p className="text-sm text-muted">Lädt …</p>}

      {query.isError && (
        <div className="flex flex-col gap-2">
          <ErrorAlert error={query.error} />
          <div>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Erneut laden
            </Button>
          </div>
        </div>
      )}

      {query.data && fenster.length === 0 && (
        <EmptyState
          title="Noch keine Wartung protokolliert"
          description="Sobald ein Wartungsfenster geöffnet und geschlossen wurde, steht es hier."
        />
      )}

      {fenster.length > 0 && (
        <ul className="flex flex-col divide-y divide-border" data-testid="history-list">
          {fenster.map((eintrag) => (
            <FensterZeile
              key={eintrag.id}
              eintrag={eintrag}
              aufgeklappt={offenesFenster === eintrag.id}
              onToggle={() =>
                setOffenesFenster((bisher) => (bisher === eintrag.id ? null : eintrag.id))
              }
            />
          ))}
        </ul>
      )}

      {dialogOffen && !readOnly && (
        <ManuelleWartungDialog
          customerId={customerId}
          open={dialogOffen}
          onClose={() => setDialogOffen(false)}
        />
      )}
    </Card>
  );
}

function FensterZeile({
  eintrag,
  aufgeklappt,
  onToggle,
}: {
  eintrag: MaintenanceSessionHistoryItem;
  aufgeklappt: boolean;
  onToggle: () => void;
}): JSX.Element {
  const offen = eintrag.status === 'open';
  return (
    <li className="py-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={aufgeklappt}
        className="flex w-full items-center gap-2 text-left hover:text-primary"
        data-testid={`history-row-${eintrag.id}`}
      >
        {aufgeklappt ? (
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span className="font-medium">{tagUndZeit(eintrag.opened_at)}</span>
        {eintrag.erfassungsart === 'manuell' && (
          <Badge tone="neutral" data-testid="badge-manuell">
            manuell erfasst
          </Badge>
        )}
        <span className="text-sm text-muted">
          {dauerText(eintrag.opened_at, eintrag.closed_at)}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {offen && <Badge tone="warning">läuft</Badge>}
          {/* had_failures kennt nur den Weg nach "kaputt" - nichts setzt es
              zurück, auch ein Force-Close nach der Reparatur nicht. Deshalb
              hier bewusst zurückhaltend formuliert: es sagt, wie das Fenster
              geschlossen wurde, nicht wie der Zustand heute ist. */}
          {eintrag.had_failures && <Badge tone="warning">mit Fehlern geschlossen</Badge>}
          {eintrag.auto_opened && <span className="text-xs text-muted">automatisch</span>}
        </span>
      </button>

      {aufgeklappt && <FensterDetail sessionId={eintrag.id} />}
    </li>
  );
}

function FensterDetail({ sessionId }: { sessionId: string }): JSX.Element {
  const detail = useMaintenanceSessionDetail(sessionId);
  const bericht = useCreateMaintenanceReportMail();
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [vorschau, setVorschau] = useState<MaintenanceReportMailResult | null>(null);

  if (detail.isLoading) {
    return <p className="mt-3 pl-6 text-sm text-muted">Lädt …</p>;
  }
  if (detail.isError) {
    return (
      <div className="mt-3 pl-6">
        <ErrorAlert error={detail.error} />
      </div>
    );
  }
  if (!detail.data) return <></>;

  const {
    session,
    runs,
    counts,
    systemeEindeutig,
    systemeGeaendert,
    berichteErzeugt,
    manuelleSysteme,
  } = detail.data;

  return (
    <div className="mt-3 flex flex-col gap-3 pl-6" data-testid={`history-detail-${sessionId}`}>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <Kennzahl label="Zeitraum">
          {formatDateTime(session.opened_at)}
          {session.closed_at ? ` – ${formatDateTime(session.closed_at)}` : ' – offen'}
        </Kennzahl>
        <Kennzahl label="Läufe">{counts.gesamt}</Kennzahl>
        {/* Bewusst systemeEindeutig statt counts: die Zähler summieren je Lauf
            und würden ein System doppelt zeigen, das zwei Routinen angefasst
            haben. Nie erreichte Systeme sind hier nicht mitgezählt. */}
        <Kennzahl label="Betreute Systeme">{systemeEindeutig}</Kennzahl>
        <Kennzahl label="davon aktualisiert">{systemeGeaendert}</Kennzahl>
      </dl>

      {/* Von Hand gewartete Systeme stecken schon in "Betreute Systeme";
          hier stehen sie namentlich, damit man sieht, WAS ohne Lauf
          gewartet wurde (16.09.2026, Hasselbach hv01/video02). */}
      {manuelleSysteme.length > 0 && (
        <p className="text-sm" data-testid={`history-manuell-${sessionId}`}>
          <span className="text-muted">Von Hand gewartet: </span>
          {manuelleSysteme.map((s) => s.name).join(', ')}
        </p>
      )}
      {session.arbeitsbeschreibung && (
        <p className="whitespace-pre-wrap text-sm">{session.arbeitsbeschreibung}</p>
      )}
      {session.durchgefuehrt_von && (
        <p className="text-xs text-muted">Durchgeführt von {session.durchgefuehrt_von}</p>
      )}
      {session.abschluss_anmerkung && (
        <p className="whitespace-pre-wrap text-sm" data-testid={`history-anmerkung-${sessionId}`}>
          <span className="text-muted">Anmerkung beim Abschluss: </span>
          {session.abschluss_anmerkung}
        </p>
      )}

      {counts.blockiert > 0 && (
        <p className="text-sm text-muted">
          {counts.blockiert} Lauf/Läufe haben ihr System nie erreicht
          {counts.unerledigt > 0 ? `, davon ${counts.unerledigt} bis heute unerledigt` : ''}.
        </p>
      )}

      {runs.length > 0 && (
        <ul className="flex flex-col gap-1">
          {runs.map((run) => (
            <li key={run.id} className="flex items-center gap-2 text-sm">
              <RunStatusBadge status={run.status} />
              <span>{run.routine_display_name}</span>
              <span className="text-muted">
                {typeof run.systeme === 'number'
                  ? run.systeme === 1
                    ? '1 System'
                    : `${run.systeme} Systeme`
                  : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Mail className="size-4" />}
          disabled={bericht.isPending}
          onClick={() => {
            setHinweis(null);
            // Erst nur ERZEUGEN (send: false) - der Dialog zeigt gleich den
            // fertigen Text. Verschickt wird nach der Bestätigung.
            bericht.mutate({ sessionId }, { onSuccess: (res) => setVorschau(res) });
          }}
          data-testid={`history-report-${sessionId}`}
        >
          {bericht.isPending ? 'Wird erstellt …' : 'Bericht für den Kunden'}
        </Button>
        {berichteErzeugt > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted">
            <MailCheck className="size-3.5" aria-hidden="true" />
            {berichteErzeugt === 1 ? '1 Bericht erstellt' : `${berichteErzeugt} Berichte erstellt`}
          </span>
        )}
      </div>

      <BerichtDialog
        bericht={vorschau}
        kunde={session.customer_name}
        onClose={() => setVorschau(null)}
        onGesendet={(text) => {
          setVorschau(null);
          setHinweis(text);
        }}
      />

      {bericht.isError && <ErrorAlert error={bericht.error} />}
      {hinweis && <p className="text-sm text-success">{hinweis}</p>}
    </div>
  );
}

function Kennzahl({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

/**
 * Bericht ansehen und senden - in einem Schritt.
 *
 * Der Betreiber hatte den Weg über den Postausgang zu Recht als Umweg
 * empfunden ("Wann haette ich das Feature bestellt?", 18.08.2026): Er will den
 * Text sehen und dann entscheiden, nicht in eine andere Ansicht wechseln. Der
 * Entwurf entsteht trotzdem - er IST das Versandprotokoll -, nur muss ihn
 * niemand mehr dort aufsuchen.
 *
 * Verworfen wird beim Abbrechen NICHT: der Entwurf bleibt im Postausgang
 * liegen und kann dort noch gesendet werden. Ein Klick auf "Abbrechen" heißt
 * "jetzt nicht", nicht "weg damit".
 */
function BerichtDialog({
  bericht,
  kunde,
  onClose,
  onGesendet,
}: {
  bericht: MaintenanceReportMailResult | null;
  kunde: string | null;
  onClose: () => void;
  onGesendet: (hinweis: string) => void;
}): JSX.Element {
  const senden = useSendNotification();
  const settings = useMailSettings();
  const testModus = isTestModeActive(settings.data);
  const ziel = testModus ? [settings.data?.redirect_address ?? ''] : (bericht?.recipients ?? []);

  return (
    <Dialog
      open={bericht != null}
      onClose={onClose}
      title={testModus ? 'Bericht senden?' : 'Bericht an den Kunden senden?'}
      description={
        testModus
          ? 'Testmodus aktiv - der Kunde bekommt nichts.'
          : `Diese Mail geht an ${kunde ?? 'den Kunden'} und lässt sich nicht zurückholen.`
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant={testModus ? 'primary' : 'danger'}
            leftIcon={<Send className="size-4" />}
            loading={senden.isPending}
            data-testid="bericht-senden"
            onClick={() => {
              if (!bericht) return;
              senden.mutate(bericht.notification_id, {
                onSuccess: (res) => {
                  const an = res.effective_recipients?.length
                    ? res.effective_recipients.join(', ')
                    : ziel.join(', ');
                  onGesendet(`Bericht gesendet an ${an}.`);
                },
              });
            }}
          >
            {testModus ? 'Senden' : 'An Kunden senden'}
          </Button>
        </>
      }
    >
      {bericht && (
        <div className="flex flex-col gap-3 text-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-muted">Betreff</dt>
            <dd className="font-medium">{bericht.subject}</dd>
            <dt className="text-muted">Geht an</dt>
            <dd className="flex flex-wrap gap-1.5">
              {ziel.filter(Boolean).map((r) => (
                <Badge key={r} tone={testModus ? 'neutral' : 'danger'} className="font-mono">
                  {r}
                </Badge>
              ))}
            </dd>
          </dl>
          {/* Der fertige Text - genau das, was rausgeht. */}
          <pre
            data-testid="bericht-text"
            className="max-h-80 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-3 font-sans"
          >
            {bericht.body}
          </pre>
          {senden.isError && <ErrorAlert error={senden.error} />}
        </div>
      )}
    </Dialog>
  );
}
